import React, { useRef } from "react";
import { HintSurfaceContext, Kbd } from "../../../components/shortcuts/Kbd";
import { useShortcut } from "../../../components/shortcuts/ShortcutProvider";
import { benchGroupAt } from "../../../game/bench-work/bench-group";
import { clampsFree } from "../../../game/Clamp";
import { machineKey } from "../../../game/Machine";
import { floorMachines } from "../../../sim/entities/MachineEntity";
import { ShortcutId } from "../../../game/shortcuts";
import { TUTORIAL_TARGET_ATTRIBUTE } from "../../../game/tutorial";
import { availableOperations } from "../../../game/skill-helpers";
import { TOOL_TYPES, ToolId } from "../../../game/Tool";
import { gatherBenchTool } from "../../../sim/commands/tool-commands";
import { machineStatesNow } from "../../../sim/machine-reads";
import { projectProgression } from "../../../sim/projection";
import { Consumables } from "../../../sim/singletons/Consumables";
import {
  BAR_CLAMP_ICON,
  GLUE_BOTTLE_ICON,
  toolIconSrc,
} from "../../../utils/uiImages";
import {
  BENCH_HOOK_ATTRIBUTE,
  BenchArrangeView,
} from "../../scenes/bench/BenchArrangeView";
import { BenchDive, BenchHand } from "../../scenes/bench/BenchDive";
import { useGame, useShellVersion } from "../../useShell";
import { useBenchChrome } from "./useBenchDive";

const BENCH_TOOL_SHORTCUTS: readonly ShortcutId[] = [
  "bench-tool-1",
  "bench-tool-2",
  "bench-tool-3",
  "bench-tool-4",
  "bench-tool-5",
  "bench-tool-6",
  "bench-tool-7",
  "bench-tool-8",
  "bench-tool-9",
];

/** The icons at the size they were drawn (64px), so they read against
 * the wood and stay pixel-exact. */
const ICON_CLASS =
  "size-16 select-none [image-rendering:pixelated] drop-shadow-[0_2px_2px_rgba(0,0,0,0.45)]";
/** An icon plus the hook button's p-1 — the box a bare hook draws. */
const HOOK_SIZE = "size-[72px]";
/** A run of hooks below a divider: the glue-up's, the neighbours'. */
const SECTION_CLASS =
  "mt-1 flex flex-col items-center gap-2 border-t border-paper-manila/20 pt-3";

/**
 * The tool rail down the left edge of the bench view — not a list in a
 * sheet, the rail itself, and the whole of tool management. Mounted
 * tools hang on it: click one to take it in hand (the tool in hand is
 * the mode, docs/bench-work.md decision 0), click it again or press
 * Escape to hang it back up. Free slots show as empty hooks — the hooks
 * are the slot count, so the rail carries no label.
 *
 * Tools get on and off the rail the way everything else on the bench
 * moves: by hand (BenchArrangeView). A tool tile carried out of the
 * arms tray (or off the shelf) and let go over an empty hook hangs
 * there — the hooks light as drop targets while a tool that fits is
 * up, brightest under the hand — and a tool dragged off its hook comes
 * down into the arms tray or onto the shelf. A tool *in hand* hangs
 * the same way by a click: the empty hooks are buttons while a tool
 * that fits is held, and pressing one (or its digit) hangs it there.
 * The rail is DOM, so it runs the press on a mounted tool itself: past
 * a few pixels of travel it hands the tool to the arranging view and
 * the hook shows empty behind it; a press that never moves is the
 * click. Each empty hook wears `BENCH_HOOK_ATTRIBUTE` so the arranging
 * view can measure it as a target.
 *
 * Where a glue-up could happen, a bar clamp and the glue bottle hang on
 * hooks of their own right after the rack's, grabbed exactly like the
 * tools — click or digit to take one in hand, again to hang it back up.
 * They never mount (a clamp is a pooled supply, the glue is free), so
 * their hooks don't count against the slots.
 *
 * Tables pushed together are one bench (bench-work/bench-group.ts), so
 * the rail shows the whole run's tools: the neighbours' hang past a
 * divider, and clicking one slides it onto this table's rack and takes
 * it in hand — the same move-then-work the pieces make. A full rack
 * refuses the slide, saying so in the hook's title.
 *
 * The rail's footprint never changes while working: a tool in hand
 * fades on its hook rather than leaving it, and the way to let go is
 * taught by the key hints along the bottom (BenchKeyHints), not
 * here. The rail is the top of the left-edge column EngineHud lays out
 * (the plan pile sits under it); BenchDive's LEFT_CHROME_PX clears the
 * column's width.
 */

/** How far a press on a mounted tool travels before it is a drag. */
const DRAG_THRESHOLD_PX = 6;

export const BenchToolRail: React.FC = () => {
  const game = useGame();
  useShellVersion();
  const chrome = useBenchChrome();
  const dive = game.entities.tryGetSingleton(BenchDive);
  const bench = dive?.openBench();
  const arrange = game.entities.tryGetSingleton(BenchArrangeView);
  const machine = bench?.view();
  const tools = machine?.state.tools ?? [];
  const toolSlots = machine?.toolSlots ?? 0;
  const freeSlots = Math.max(0, toolSlots - tools.length);
  // Tools change what the station can do, so the rail holds still until
  // the running job is off it.
  const working = machine?.operationProgress.status === "inProgress";
  // The hand, for the hooks: a tool up — carried by a press, or held as
  // the mode — that would hang here arms every free hook, and the one
  // under the hand is hot. A tool dragged off this rail leaves its hook
  // bare behind it.
  const dragSource = arrange?.dragSource() ?? null;
  const hooksArmed = arrange?.hookAccepts() ?? false;
  const hotHook = arrange?.dropHookIndex() ?? null;
  const liftedHook = dragSource?.kind === "hook" ? dragSource.index : null;
  // A bare hook takes the tool in hand on a click (or its digit).
  const hangsHeld = hooksArmed && dive?.heldTool != null && !dragSource;
  const hangHeld = (index: number) => {
    if (!hangsHeld) return;
    arrange?.hangHeldTool(index);
  };
  // The hook the tutorial points at: the first one showing bare.
  const firstBareHook = liftedHook ?? tools.length;

  // The press on a mounted tool: a click takes it in hand, but past a
  // few pixels of travel it is a drag, and the tool goes to the hand
  // on the canvas instead (BenchArrangeView). The engine's mouse never
  // sees a press that starts on the rail, so this runs the whole press
  // on the window and ends the drag itself on release.
  const dragged = useRef(false);
  const pressTool = (
    event: React.MouseEvent<HTMLButtonElement>,
    toolId: ToolId,
    index: number,
  ) => {
    if (event.button !== 0 || !arrange || !dive || dive.handsFull()) return;
    if (working) return;
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const rect = event.currentTarget.getBoundingClientRect();
    let begun = false;
    const onMove = (move: MouseEvent) => {
      if (begun) return;
      if (
        Math.hypot(move.clientX - startX, move.clientY - startY) <
        DRAG_THRESHOLD_PX
      ) {
        return;
      }
      begun = true;
      arrange.beginHookDrag(
        toolId,
        index,
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
      );
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (!begun) return;
      // A release back over the button fires its click right after this
      // mouseup, and that click must not take the tool in hand; a
      // release anywhere else fires no click on it at all, so the flag
      // clears itself once the event has had its turn.
      dragged.current = true;
      setTimeout(() => {
        dragged.current = false;
      }, 0);
      arrange.endHookDrag();
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  // The rest of the run's tools, hung past the divider.
  const neighbourTools = machine
    ? benchGroupAt(floorMachines(game), machine)
        .members.map((member) => member.machine)
        .filter(
          (member) =>
            machineKey(member.state) !== machineKey(machine.state) &&
            member.state.operationProgress.status !== "inProgress",
        )
        .flatMap((member) =>
          member.state.tools
            .filter((toolId) => {
              const compatible = TOOL_TYPES[toolId].compatibleMachines;
              return (
                !compatible || compatible.includes(machine.state.machineTypeId)
              );
            })
            .map((toolId) => ({ toolId, fromKey: machineKey(member.state) })),
        )
    : [];
  const railFull = freeSlots <= 0;

  // The clamp and the bottle hang right after the rack's hooks,
  // wherever a glue-up could happen.
  const glue = machine
    ? availableOperations(machine, projectProgression(game)).some(
        (operation) => operation.interaction?.kind === "glue",
      )
    : false;
  const consumables = game.entities.tryGetSingleton(Consumables);
  const clampsAvailable = consumables
    ? clampsFree(consumables.clamps, machineStatesNow(game))
    : 0;
  const clampHook = glue ? toolSlots : -1;
  const glueHook = glue ? toolSlots + 1 : -1;

  // The digits answer to the hooks in rail order: digit N does what
  // clicking the Nth hook does, whether that's a mounted tool, a bare
  // hook with a tool in hand to hang on it, or the clamp and bottle
  // after them. Registered unconditionally (hooks), enabled per hook
  // exactly when its click would work.
  for (const [index, shortcutId] of BENCH_TOOL_SHORTCUTS.entries()) {
    const mounted: ToolId | undefined = tools[index];
    const bare = mounted == null && index < toolSlots;
    const grab: BenchHand | null =
      index === clampHook
        ? { kind: "clamp" }
        : index === glueHook
          ? { kind: "glue" }
          : null;
    // eslint-disable-next-line react-hooks/rules-of-hooks -- fixed-length list
    useShortcut(
      shortcutId,
      () => {
        if (mounted) {
          dive?.toggleHand({
            kind: "tool",
            toolId: mounted,
            from: { kind: "hook" },
          });
        } else if (bare) hangHeld(index);
        else if (grab) dive?.toggleHand(grab);
      },
      bench != null &&
        !chrome.inert &&
        (mounted != null ||
          (bare && hangsHeld) ||
          (grab != null &&
            (grab.kind !== "clamp" ||
              clampsAvailable > 0 ||
              (dive?.holdingClamp ?? false)))),
    );
  }

  if (!dive || !bench || !machine) return null;
  if (toolSlots === 0 && !glue) return null;

  return (
    // The rail is dark workshop chrome, so the hooks' number caps take
    // the chrome face.
    <HintSurfaceContext.Provider value="chrome">
      <div
        className={`pointer-events-auto flex flex-col items-center gap-2 rounded border-2 border-black/40 bg-[#4a3826]/95 px-1.5 py-2 shadow-lg ${chrome.className}`}
        data-testid="bench-tool-rail"
        inert={chrome.inert}
      >
        {tools.map((toolId, index) => {
          const held = dive.heldTool === toolId;
          // The tool the hand lifted off: its hook shows bare while it's
          // carried, and takes it back.
          if (liftedHook === index) {
            return (
              <BareHook
                key={`${toolId}-${index}`}
                index={index}
                armed={hooksArmed}
                hot={hotHook === index}
                hookNumber={index + 1}
                first={index === firstBareHook}
                onHang={hangsHeld ? () => hangHeld(index) : undefined}
              />
            );
          }
          return (
            <span key={`${toolId}-${index}`} className="relative">
              <button
                type="button"
                data-testid={`bench-tool-${toolId}`}
                aria-label={
                  held
                    ? `Hang up the ${TOOL_TYPES[toolId].name}`
                    : `Pick up the ${TOOL_TYPES[toolId].name}`
                }
                title={TOOL_TYPES[toolId].name}
                data-sfx="ui-tab"
                onMouseDown={(event) => pressTool(event, toolId, index)}
                onClick={(event) => {
                  event.stopPropagation();
                  // A press that became a drag is not a click
                  if (dragged.current) return;
                  dive.toggleHand({
                    kind: "tool",
                    toolId,
                    from: { kind: "hook" },
                  });
                }}
                className={`cursor-pointer rounded p-1 transition-transform hover:-translate-y-0.5 hover:drop-shadow-[0_3px_4px_rgba(0,0,0,0.5)] ${
                  held ? "opacity-30" : ""
                }`}
              >
                <img
                  src={toolIconSrc(toolId)}
                  alt=""
                  draggable={false}
                  className={ICON_CLASS}
                />
              </button>
              {index < BENCH_TOOL_SHORTCUTS.length && (
                <Kbd className="pointer-events-none absolute -bottom-1 -left-1">
                  {index + 1}
                </Kbd>
              )}
            </span>
          );
        })}

        {Array.from({ length: freeSlots }, (_, index) => {
          const hookIndex = tools.length + index;
          return (
            <BareHook
              key={`hook-${index}`}
              index={hookIndex}
              armed={hooksArmed}
              hot={hotHook === hookIndex}
              hookNumber={hookIndex + 1}
              first={hookIndex === firstBareHook}
              onHang={hangsHeld ? () => hangHeld(hookIndex) : undefined}
            />
          );
        })}

        {/* The clamp and the bottle, on hooks of their own where a
            glue-up could happen — grabbed like any other tool, hung
            back up the same way */}
        {glue && (
          <span className={SECTION_CLASS}>
            <GrabHook
              testId="bench-clamp-supply"
              iconSrc={BAR_CLAMP_ICON}
              name="bar clamp"
              title={`Bar clamps — ${clampsAvailable} free on the rack`}
              held={dive.holdingClamp}
              hookNumber={clampHook + 1}
              disabled={clampsAvailable === 0 && !dive.holdingClamp}
              onClick={() => dive.toggleHand({ kind: "clamp" })}
            />
            <GrabHook
              testId="bench-glue-bottle"
              iconSrc={GLUE_BOTTLE_ICON}
              name="glue bottle"
              title="Wood glue — run a bead down each open seam"
              held={dive.holdingGlue}
              hookNumber={glueHook + 1}
              onClick={() => dive.toggleHand({ kind: "glue" })}
            />
          </span>
        )}

        {/* The neighbours' tools, past a divider: one rail for the whole
            run. Clicking slides the tool onto this table's rack and
            takes it in hand */}
        {neighbourTools.length > 0 && (
          <span className={SECTION_CLASS}>
            {neighbourTools.map(({ toolId, fromKey }, index) => (
              <button
                key={`${fromKey}-${toolId}-${index}`}
                type="button"
                data-testid={`bench-run-tool-${toolId}`}
                aria-label={`Bring the ${TOOL_TYPES[toolId].name} to this bench`}
                title={
                  railFull
                    ? "The rail is full — take a tool off first"
                    : "On the next table over — click to bring it here"
                }
                disabled={working || railFull}
                onClick={(event) => {
                  event.stopPropagation();
                  if (gatherBenchTool(game, bench, toolId)) {
                    if (dive.heldTool !== toolId) dive.takeRailTool(toolId);
                  }
                }}
                className={`rounded p-1 opacity-60 transition-transform ${
                  working || railFull
                    ? "cursor-default opacity-40"
                    : "cursor-pointer hover:-translate-y-0.5 hover:opacity-100 hover:drop-shadow-[0_3px_4px_rgba(0,0,0,0.5)]"
                }`}
              >
                <img
                  src={toolIconSrc(toolId)}
                  alt=""
                  draggable={false}
                  className={ICON_CLASS}
                />
              </button>
            ))}
          </span>
        )}
      </div>
    </HintSurfaceContext.Provider>
  );
};

/**
 * An empty hook: the slot is real, there's just nothing on it. It is
 * the drop target for a carried tool (measured off the page by
 * `BENCH_HOOK_ATTRIBUTE`): armed while a tool that fits is up, hot
 * under the hand. With a tool held in hand it is a button too — a click
 * hangs the tool here (`onHang`).
 */
const BareHook: React.FC<{
  index: number;
  armed: boolean;
  hot: boolean;
  hookNumber: number;
  /** The first bare hook is where the tutorial points a tool at. */
  first: boolean;
  /** Hang the tool in hand here, while one that fits is held. */
  onHang?: () => void;
}> = ({ index, armed, hot, hookNumber, first, onHang }) => (
  <span className="relative">
    <button
      type="button"
      {...{ [BENCH_HOOK_ATTRIBUTE]: index }}
      {...(first ? { [TUTORIAL_TARGET_ATTRIBUTE]: "bench-hook" } : {})}
      data-testid={`bench-hook-${index}`}
      data-armed={armed ? "" : undefined}
      data-hot={hot ? "" : undefined}
      aria-label={onHang ? "Hang the tool here" : "An empty hook"}
      title={
        onHang
          ? "Click to hang the tool here"
          : armed
            ? "Let go to hang the tool here"
            : "An empty hook — carry a tool here to hang it"
      }
      // Never `disabled`: the browser drops the mouse events on a
      // disabled control, and a tool carried onto this hook is let go
      // by the release the arranging view listens for on the window.
      aria-disabled={!onHang}
      tabIndex={onHang ? 0 : -1}
      data-sfx={onHang ? "ui-tab" : undefined}
      onClick={(event) => {
        event.stopPropagation();
        onHang?.();
      }}
      className={`flex ${HOOK_SIZE} items-center justify-center rounded border border-dashed transition-colors ${
        onHang ? "cursor-pointer" : "cursor-default"
      } ${
        hot
          ? "border-gold-light bg-gold-light/20"
          : armed
            ? "border-paper-manila/70 bg-paper-manila/10"
            : "border-paper-manila/25"
      }`}
    >
      <span
        className={`size-1.5 rounded-full ${
          hot ? "bg-gold-light" : "bg-paper-manila/30"
        }`}
      />
    </button>
    {hookNumber <= BENCH_TOOL_SHORTCUTS.length && (
      <Kbd className="pointer-events-none absolute -bottom-1 -left-1">
        {hookNumber}
      </Kbd>
    )}
  </span>
);

/** The clamp's and the bottle's hooks — the same icon-on-a-hook the
 * tools get (nothing mounts them, nothing takes them off the rail). */
const GrabHook: React.FC<{
  testId: string;
  iconSrc: string;
  name: string;
  title: string;
  held: boolean;
  hookNumber: number;
  disabled?: boolean;
  onClick: () => void;
}> = ({
  testId,
  iconSrc,
  name,
  title,
  held,
  hookNumber,
  disabled,
  onClick,
}) => (
  <span className="relative">
    <button
      type="button"
      data-testid={testId}
      aria-label={held ? `Put the ${name} back` : `Pick up the ${name}`}
      title={title}
      disabled={disabled}
      data-sfx="ui-tab"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={`cursor-pointer rounded p-1 transition-transform hover:-translate-y-0.5 hover:drop-shadow-[0_3px_4px_rgba(0,0,0,0.5)] disabled:cursor-default disabled:opacity-40 disabled:hover:translate-y-0 ${
        held ? "opacity-30" : ""
      }`}
    >
      <img src={iconSrc} alt="" draggable={false} className={ICON_CLASS} />
    </button>
    {hookNumber >= 1 && hookNumber <= BENCH_TOOL_SHORTCUTS.length && (
      <Kbd className="pointer-events-none absolute -bottom-1 -left-1">
        {hookNumber}
      </Kbd>
    )}
  </span>
);
